import { Installation } from 'interfaces';
import { Dispatch, SetStateAction } from 'react';
import { PacksService } from 'services/Packs/PacksService';
import { ReportService } from 'services/Reports/ReportService';
import { AttendanceMetrics, MetricsLoadingProps } from 'typings';
import { TicketsInfo } from 'typings/services/TicketsInfo';
import { nameof, retryBatchRequest } from 'utils';

export const updateAttendanceMetrics = (
    result: AttendanceMetrics,
    isFirstIteration: boolean,
    actualAttendanceMetrics: AttendanceMetrics | undefined,
    setAttendanceMetrics: Dispatch<SetStateAction<AttendanceMetrics | undefined>>,
) => {
    if (isFirstIteration || !actualAttendanceMetrics) return setAttendanceMetrics(result);
    const metrics: AttendanceMetrics = JSON.parse(JSON.stringify(actualAttendanceMetrics));

    setAttendanceMetrics(prevState => {
        if (!prevState) return metrics;
        metrics.attendants.online.amount = result.attendants.online.amount;
        metrics.attendants.pause.amount = result.attendants.pause.amount;
        metrics.attendants.invisible.amount = result.attendants.invisible.amount;

        metrics.attendants.ticketsPerAttendant.amount = result.attendants.ticketsPerAttendant.amount;

        metrics.tickets.open.amount = result.tickets.open.amount + prevState.tickets.open.amount;
        metrics.tickets.closed.amount = result.tickets.closed.amount + prevState.tickets.closed.amount;
        metrics.tickets.closedByAttendant.amount =
            result.tickets.closedByAttendant.amount + prevState.tickets.closedByAttendant.amount;
        metrics.tickets.closedByClient.amount = result.tickets.closedByClient.amount;
        metrics.tickets.missed.amount = result.tickets.missed.amount + prevState.tickets.missed.amount;

        metrics.tickets.waiting.amount = result.tickets.waiting.amount + prevState.tickets.waiting.amount;

        metrics.tickets.waiting.timeMetrics.queueTime.avg = result.tickets.waiting.timeMetrics.queueTime.avg;
        metrics.tickets.waiting.timeMetrics.queueTime.max = result.tickets.waiting.timeMetrics.queueTime.max;
        metrics.tickets.inAttendance.amount =
            result.tickets.inAttendance.amount + prevState.tickets.inAttendance.amount;
        metrics.tickets.inAttendance.timeMetrics.firstResponseTime.max =
            result.tickets.inAttendance.timeMetrics.firstResponseTime.max;
        metrics.tickets.inAttendance.timeMetrics.firstResponseTime.avg =
            result.tickets.inAttendance.timeMetrics.firstResponseTime.avg;

        return metrics;
    });
};

interface BatchRequestCallbacks {
    setAttendanceMetrics: Dispatch<SetStateAction<AttendanceMetrics | undefined>>;
    setMetricsLoading: Dispatch<SetStateAction<MetricsLoadingProps>>;
    updateAttendanceMetrics: (
        result: AttendanceMetrics,
        isFirstIteration: boolean,
        actualAttendanceMetrics: AttendanceMetrics | undefined,
        setAttendanceMetrics: Dispatch<SetStateAction<AttendanceMetrics | undefined>>,
    ) => void;
}

export const callBackBatchRequest = (
    result: AttendanceMetrics,
    amount: number,
    countBatchAttendanceMetrics: { value: number },
    totalAmount: number,
    actualAttendanceMetrics: AttendanceMetrics | undefined,
    callbacks: BatchRequestCallbacks,
) => {
    countBatchAttendanceMetrics.value += amount;
    const isTheFirstBatch = countBatchAttendanceMetrics.value === amount;
    callbacks.updateAttendanceMetrics(result, isTheFirstBatch, actualAttendanceMetrics, callbacks.setAttendanceMetrics);

    const percentage = Math.floor((countBatchAttendanceMetrics.value / totalAmount) * 100);
    callbacks.setMetricsLoading(prevState => ({
        ...prevState,
        // isAttendanceMetricsLoading is intentionally kept true here.
        // It will only be set to false by the finally block in fetchAttendanceMetrics,
        // after getAttendantStatusCounts resolves — preventing zeros from being displayed.
        isAttendanceMetricsLoading: true,
        attendanceMetricsPercentageLoaded: percentage,
    }));
};

export const fetchAttendanceMetrics = async (
    setMetricsLoading: Dispatch<SetStateAction<MetricsLoadingProps>>,
    reportApi: ReportService,
    bots: Installation[],
    actualAttendanceMetrics: AttendanceMetrics | undefined,
    setAttendanceMetrics: Dispatch<SetStateAction<AttendanceMetrics | undefined>>,
    abortController?: AbortController,
) => {
    try {
        const batchSize = 10;
        const botShortNames = bots.map(b => b.transhipmentSkill);
        // eslint-disable-next-line prefer-const
        let countBatchAttendanceMetrics = { value: 0 };

        if (abortController?.signal.aborted) {
            return;
        }

        setMetricsLoading(prevState => ({
            ...prevState,
            isAttendanceMetricsLoading: true,
            attendanceMetricsPercentageLoaded: 0,
        }));

        await retryBatchRequest<AttendanceMetrics>(
            reportApi,
            nameof<ReportService>('getAttendanceMetrics'),
            (items: any[]) => [items],
            (result: AttendanceMetrics, amount: number) => {
                if (abortController?.signal.aborted) {
                    return;
                }
                return callBackBatchRequest(
                    result,
                    amount,
                    countBatchAttendanceMetrics,
                    bots.length,
                    actualAttendanceMetrics,
                    {
                        setAttendanceMetrics,
                        setMetricsLoading,
                        updateAttendanceMetrics,
                    },
                );
            },
            botShortNames,
            batchSize,
        );

        if (!abortController?.signal.aborted) {
            const statusCounts = await reportApi.getAttendantStatusCounts(botShortNames);
            if (abortController?.signal.aborted) {
                return;
            }
            setAttendanceMetrics(prevState => {
                if (!prevState) return prevState;
                return {
                    ...prevState,
                    attendants: {
                        ...prevState.attendants,
                        online: { amount: statusCounts.online },
                        invisible: { amount: statusCounts.invisible },
                        pause: { amount: statusCounts.pause },
                    },
                };
            });
        }
    } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error fetching attendance metrics:', error);
        }
    } finally {
        if (!abortController?.signal.aborted) {
            setMetricsLoading(prevState => ({
                ...prevState,
                isAttendanceMetricsLoading: false,
            }));
        }
    }
};

export const fetchTicketsInfo = async (
    setTicketsInfo: Dispatch<SetStateAction<TicketsInfo | undefined>>,
    setMetricsLoading: Dispatch<SetStateAction<MetricsLoadingProps>>,
    bots: Installation[],
    packsService: PacksService,
    abortController?: AbortController,
) => {
    try {
        const installationsIds = bots.map(b => b.installationId);

        if (abortController?.signal.aborted) {
            return;
        }

        setMetricsLoading(prevState => ({
            ...prevState,
            isTicketMetricsLoading: true,
            ticketMetricsPercentageLoaded: 0,
        }));

        const response = await packsService.getTicketInfo(installationsIds);

        if (!abortController?.signal.aborted) {
            setTicketsInfo(response);
        }
    } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error fetching tickets info:', error);
        }
        return;
    } finally {
        if (!abortController?.signal.aborted) {
            setMetricsLoading(prevState => ({
                ...prevState,
                isTicketMetricsLoading: false,
                ticketMetricsPercentageLoaded: 100,
            }));
        }
    }
};
